import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { useI18n } from '../../i18n'
import { useScrollAnim } from '../../hooks/use-scroll-anim'

interface FAQItem {
  questionZh: string
  questionEn: string
  answerZh: string
  answerEn: string
}

const faqs: FAQItem[] = [
  {
    questionZh: '如何选择适合的套餐？',
    questionEn: 'How do I choose the right plan?',
    answerZh: '根据团队规模和使用场景选择。5人以下轻度使用可选体验版；20人团队需要知识库和文件分析选专业版；100人以上大型团队建议旗舰版；有私有化部署和定制需求选企业定制。',
    answerEn: 'Choose based on team size and use case. Trial for <5 light users; Professional for 20-person teams needing knowledge base; Flagship for 100+ large teams; Enterprise for private deployment and custom needs.',
  },
  {
    questionZh: '企业套餐和个人套餐有什么区别？',
    questionEn: 'What is the difference between enterprise and user plans?',
    answerZh: '企业套餐由企业管理员购买，企业下所有用户共享Token额度池。个人套餐是用户独立购买的加量包，额度独立计算不与企业共享。',
    answerEn: 'Enterprise plans are purchased by admins and shared among all team members. User plans are individual add-ons with separate quotas not shared with the enterprise pool.',
  },
  {
    questionZh: '支持哪些支付方式？',
    questionEn: 'What payment methods are supported?',
    answerZh: '目前支持支付宝和微信支付。PC端可扫码支付，移动端自动跳转支付页面。',
    answerEn: 'We support Alipay and WeChat Pay. Scan QR code on desktop, auto-redirect on mobile.',
  },
  {
    questionZh: 'Token 额度用完了怎么办？',
    questionEn: 'What happens when tokens run out?',
    answerZh: '额度用完后可升级套餐或购买加量包，体验版用户每日额度会自动恢复。',
    answerEn: 'Upgrade your plan or purchase add-on packs. Trial users get daily quota refresh.',
  },
  {
    questionZh: '可以随时更换套餐吗？',
    questionEn: 'Can I change plans at any time?',
    answerZh: '可以随时升级，升级后立即生效，剩余天数按比例折算。降级需等到当前计费周期结束。',
    answerEn: 'Upgrade anytime with immediate effect and prorated billing. Downgrades take effect at the end of the current billing cycle.',
  },
  {
    questionZh: '年付有什么优惠？',
    questionEn: 'Is there a discount for annual billing?',
    answerZh: '年付享受8折优惠，相当于免费使用2.4个月。专业版年付¥2,399/月，旗舰版年付¥7,999/月。',
    answerEn: 'Annual billing saves 20%, equivalent to 2.4 months free. Professional: ¥2,399/mo annually, Flagship: ¥7,999/mo annually.',
  },
]

function FAQAccordionItem({ item, isOpen, onToggle }: { item: FAQItem; isOpen: boolean; onToggle: () => void }) {
  const { t } = useI18n()
  return (
    <div className="border-b border-black/[0.04] last:border-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-4 text-left"
      >
        <span className="text-sm font-medium text-[#1a1a1a] pr-4">
          {t(item.questionZh, item.questionEn)}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-gray-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-40 pb-4' : 'max-h-0'}`}>
        <p className="text-sm text-gray-500 leading-relaxed">
          {t(item.answerZh, item.answerEn)}
        </p>
      </div>
    </div>
  )
}

export function PricingFAQ() {
  const { t } = useI18n()
  const { ref, isVisible } = useScrollAnim()
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  return (
    <section ref={ref} className="py-8">
      <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <h2 className="text-2xl font-bold text-[#1a1a1a] text-center mb-8">
          {t('常见问题', 'FAQ')}
        </h2>
        <div className="max-w-2xl mx-auto bg-white rounded-2xl border border-black/[0.04] px-6">
          {faqs.map((item, i) => (
            <FAQAccordionItem
              key={i}
              item={item}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
            />
          ))}
        </div>
      </div>
    </section>
  )
}
